import { useMemo } from "react";
import { AnalysisData } from "@/services/statistical-analysis-service";
import { BaseAlertState } from "../base/BaseAlert.types";
import { useAlertState } from "../base/useAlertState";
import { formatCurrency } from "@/utils/format/currency";
import { checkSampleSize } from "../utils";

export interface UsePriceAlertStateParams {
  analysis?: AnalysisData;
  loading?: boolean;
  hasRequiredData?: boolean;
  neighborhood?: string | null;
  sizeM2?: string;
  capacity?: string;
  monthlyPrice?: string;
  sampleSize?: number;
  minSampleSize?: number;
}

export const usePriceAlertState = ({
  analysis,
  loading = false,
  hasRequiredData = true,
  neighborhood,
  sizeM2,
  capacity,
  monthlyPrice,
  sampleSize,
  minSampleSize = 5,
}: UsePriceAlertStateParams): BaseAlertState | null => {
  const hasSizeAndCapacity = Boolean(
    sizeM2 &&
      capacity &&
      sizeM2.trim() !== "" &&
      capacity.trim() !== "" &&
      parseFloat(sizeM2) > 0 &&
      parseFloat(capacity) > 0,
  );

  const calculatePriceRange = () => {
    if (!hasSizeAndCapacity || !sizeM2 || !capacity) return null;

    const size = parseFloat(sizeM2);
    const cap = parseFloat(capacity);

    const minPricePerM2 = 20;
    const maxPricePerM2 = 40;

    const basePrice = size * minPricePerM2;
    const maxPrice = size * maxPricePerM2;

    const capacityMultiplier = Math.max(1, cap / 10);

    const minRef = Math.round(basePrice * capacityMultiplier);
    const maxRef = Math.round(maxPrice * capacityMultiplier);

    return { minRef, maxRef };
  };

  const { baseState, priceSpecificState } = useMemo(() => {
    if (loading) {
      return {
        baseState: {
          loading: true,
          loadingMessage: "Analisando dados do bairro...",
        },
        priceSpecificState: null,
      };
    }

    if (hasSizeAndCapacity && !hasRequiredData) {
      const priceRange = calculatePriceRange();
      const message = priceRange
        ? `Baseado no tamanho e capacidade, sugerimos entre ${formatCurrency(priceRange.minRef)} - ${formatCurrency(priceRange.maxRef)}/mês. Para uma análise mais precisa, selecione o bairro.`
        : "Preencha o bairro para análise mais precisa baseada no mercado local.";

      return {
        baseState: {
          loading: false,
          hasData: true,
          infoMessage: message,
        },
        priceSpecificState: {
          type: "info" as const,
          title: "Sugestão de preço",
        },
      };
    }

    if (!hasRequiredData) {
      return {
        baseState: {
          loading: false,
          hasData: false,
          infoMessage:
            "Preencha o tamanho e a capacidade da sala. Com essas informações, vamos indicar uma faixa de preço ideal para você.",
        },
        priceSpecificState: {
          type: "info" as const,
          title: "Sugestão de preço",
        },
      };
    }

    const sampleSizeCheck = checkSampleSize(sampleSize, minSampleSize);

    if (sampleSizeCheck.hasInsufficientSample) {
      return {
        baseState: {
          loading: false,
          hasData: false,
          warningMessage: sampleSizeCheck.warningMessage,
        },
        priceSpecificState: {
          type: "warning" as const,
          title: "Dados insuficientes para análise",
        },
      };
    }

    if (!analysis) {
      const insufficientDataMessage = `Dados insuficientes para análise do bairro ${
        neighborhood || "selecionado"
      }. Tente com outro espaço ou verifique os dados inseridos.`;

      return {
        baseState: {
          loading: false,
          hasData: false,
          warningMessage: insufficientDataMessage,
        },
        priceSpecificState: {
          type: "warning" as const,
          title: "Dados insuficientes para análise",
        },
      };
    }

    const { outlierDetection, recommendations } = analysis;

    const hasValidPrice = (price: string): boolean => {
      if (!price || price.trim() === "") return false;
      const cleanedValue = price.replace(/\./g, "").replace(",", ".");
      const numValue = parseFloat(cleanedValue) || 0;
      return numValue > 0;
    };
    const hasMonthlyPrice = monthlyPrice && hasValidPrice(monthlyPrice);

    if (recommendations && !hasMonthlyPrice) {
      const message = `Salas semelhantes, considerando localização, tamanho e capacidade, costumam custar entre ${formatCurrency(
        recommendations.suggestedMin,
      )}-${formatCurrency(recommendations.suggestedMax)}/mês.`;

      return {
        baseState: {
          loading: false,
          hasData: true,
          infoMessage: message,
        },
        priceSpecificState: {
          type: "infoBlue" as const,
          title: "Sugestão de preço",
        },
      };
    }

    if (!outlierDetection || !recommendations) {
      return {
        baseState: {
          loading: false,
          hasData: true,
          infoMessage:
            "Análise parcial disponível. Complete todos os campos para análise completa.",
        },
        priceSpecificState: {
          type: "info" as const,
          title: "Sugestão de preço",
        },
      };
    }

    switch (outlierDetection.status) {
      case "within_range": {
        const message = `Salas semelhantes custam entre ${formatCurrency(
          recommendations.suggestedMin,
        )} - ${formatCurrency(recommendations.suggestedMax)}/mês`;

        return {
          baseState: {
            loading: false,
            hasData: true,
            successMessage: message,
          },
          priceSpecificState: {
            type: "success" as const,
            title: "Valor dentro da média",
          },
        };
      }

      case "above_outlier": {
        const message = `Salas semelhantes custam entre ${formatCurrency(
          recommendations.suggestedMin,
        )} - ${formatCurrency(recommendations.suggestedMax)}/mês`;

        return {
          baseState: {
            loading: false,
            hasData: true,
            hasError: true,
            errorMessage: message,
          },
          priceSpecificState: {
            type: "error" as const,
            title: "Valor acima da média",
          },
        };
      }

      case "below_outlier": {
        const message = `Salas semelhantes custam entre ${formatCurrency(
          recommendations.suggestedMin,
        )} - ${formatCurrency(recommendations.suggestedMax)}/mês`;

        return {
          baseState: {
            loading: false,
            hasData: true,
            hasError: true,
            errorMessage: message,
          },
          priceSpecificState: {
            type: "error" as const,
            title: "Valor abaixo da média",
          },
        };
      }

      default: {
        return {
          baseState: {
            loading: false,
            hasData: true,
            infoMessage: outlierDetection.message || "",
          },
          priceSpecificState: {
            type: "info" as const,
            title: "Sugestão de preço",
          },
        };
      }
    }
  }, [
    analysis,
    loading,
    hasRequiredData,
    neighborhood,
    sizeM2,
    capacity,
    hasSizeAndCapacity,
    sampleSize,
    minSampleSize,
  ]);

  const alertState = useAlertState(baseState);

  return useMemo(() => {
    if (!alertState || !priceSpecificState) return alertState;

    return {
      ...alertState,
      type: priceSpecificState.type,
      title: priceSpecificState.title,
    };
  }, [alertState, priceSpecificState]);
};
